/**
 * Roles Service
 *
 * React Query hooks for RBAC role management API
 */

import { useQuery, useMutation } from '@tanstack/react-query';
import axios from 'axios';

import { noop } from '../lib/serviceHelpers';

/**
 * Fetch all roles with their permissions
 * Requires: users:manage_roles or roles:edit permission
 */
export const useFetchRoles = () => {
  return useQuery(
    ['roles'],
    async () => {
      const res = await axios.get('/admin/roles');
      return res.data;
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 30000,
    }
  );
};

/**
 * Fetch all available permissions
 * Requires: users:manage_roles or roles:edit permission
 */
export const useFetchAllPermissions = () => {
  return useQuery(
    ['permissions'],
    async () => {
      const res = await axios.get('/admin/roles/permissions');
      return res.data;
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 60000,
    }
  );
};

/**
 * Fetch roles for a specific user
 * Requires: users:view permission
 */
export const useFetchUserRoles = (userId) => {
  return useQuery(
    ['user-roles', userId],
    async () => {
      const res = await axios.get(`/admin/roles/user/${userId}`);
      return res.data;
    },
    {
      enabled: !!userId,
      refetchOnWindowFocus: false,
      staleTime: 5000,
    }
  );
};

/**
 * Bulk set all roles for a user (replace)
 * Requires: users:manage_roles permission
 */
export const useSetUserRoles = (callback, opts = {}) => {
  return useMutation(
    async ({ userId, roleNames }) => {
      const res = await axios.put(`/admin/roles/user/${userId}`, {
        roleNames,
      });
      return res.data;
    },
    { ...opts, onSuccess: callback || noop }
  );
};

// ============================================================================
// PERMISSION OVERRIDES
// ============================================================================

/**
 * Fetch permission overrides for a user
 * Returns: { overrides: [...], rolePermissions: [...] }
 * Requires: users:view permission
 */
export const useFetchUserPermissionOverrides = (userId) => {
  return useQuery(
    ['user-permission-overrides', userId],
    async () => {
      const res = await axios.get(`/admin/roles/user/${userId}/overrides`);
      return res.data;
    },
    {
      enabled: !!userId,
      refetchOnWindowFocus: false,
      staleTime: 5000,
    }
  );
};

/**
 * Bulk set permission overrides for a user
 * Requires: users:manage_roles permission
 */
export const useSetUserPermissionOverrides = (callback, opts = {}) => {
  return useMutation(
    async ({ userId, overrides }) => {
      const res = await axios.put(`/admin/roles/user/${userId}/overrides`, {
        overrides,
      });
      return res.data;
    },
    { ...opts, onSuccess: callback || noop }
  );
};

// ============================================================================
// ROLE DEFINITION CRUD
// ============================================================================

/**
 * Create a new role
 * Requires: roles:edit permission
 */
export const useCreateRole = (callback, opts = {}) => {
  return useMutation(
    async ({ roleName, displayName, description, roleGroup, permissions }) => {
      const res = await axios.post('/admin/roles', {
        roleName,
        displayName,
        description,
        roleGroup,
        permissions,
      });
      return res.data;
    },
    { ...opts, onSuccess: callback || noop }
  );
};

/**
 * Update role metadata
 * Requires: roles:edit permission
 */
export const useUpdateRole = (callback, opts = {}) => {
  return useMutation(
    async ({ roleId, displayName, description, order }) => {
      const res = await axios.patch(`/admin/roles/${roleId}`, {
        displayName,
        description,
        order,
      });
      return res.data;
    },
    { ...opts, onSuccess: callback || noop }
  );
};

/**
 * Delete a role
 * Requires: roles:edit permission
 */
export const useDeleteRole = (callback, opts = {}) => {
  return useMutation(
    async ({ roleId, force }) => {
      const url = force
        ? `/admin/roles/${roleId}?force=true`
        : `/admin/roles/${roleId}`;
      try {
        const res = await axios.delete(url);
        return res.data;
      } catch (err) {
        if (err.response?.status === 409) {
          return { conflict: true, userCount: err.response.data?.userCount || 0 };
        }
        throw err;
      }
    },
    { ...opts, onSuccess: callback || noop }
  );
};

/**
 * Update permissions for a role
 * Requires: roles:edit permission
 */
export const useUpdateRolePermissions = (callback, opts = {}) => {
  return useMutation(
    async ({ roleId, permissionKeys }) => {
      const res = await axios.put(`/admin/roles/${roleId}/permissions`, {
        permissionKeys,
      });
      return res.data;
    },
    { ...opts, onSuccess: callback || noop }
  );
};
